<template>
  <transition
    @enter="onEnter"
    @after-leave="onAfterLeave"
  >
    <div
      v-show="visible"
      ref="el"
      :class="[
        $style.base,
        {
          [$style.alignLeft]: alignX === 'left',
          [$style.alignRight]: alignX === 'right',
          [$style.alignBottom]: alignY === 'bottom',
          [$style.alignTop]: alignY === 'top'
        }
      ]"
      v-test="'_base-button-popover'"
    >
      <div :class="$style.inner">
        <component
          :is="
            option.routerLink
              ? 'router-link'
              : option.submitForm
                ? 'button'
                : 'div'
          "
          v-for="(option, index) in options"
          :key="index"
          :to="option.routerLink"
          :class="[$style.option, { [$style.colorError]: option.color === 'error' }]"
          :data-test-value="option.value"
          @click="$emit('select', option.value)"
          v-test="'_base-button-popover-option'"
        >
          <BaseIcon
            v-if="option.icon"
            :name="option.icon"
            :color="option.color === 'error' ? 'error' : 'default'"
          />
          {{ option.label }}
        </component>
      </div>
    </div>
  </transition>
</template>

<script
  setup
  lang="ts"
>
import { onClickOutside } from '@vueuse/core';
import { ref } from 'vue';
import type { Option } from './types';

const props = defineProps<{
  visible: boolean;
  options: Option[];
  parentEl: any;
}>();

const emit = defineEmits(['close', 'select']);

const el = ref();
onClickOutside(
  el,
  () => {
    emit('close');
  },
  {
    ignore: [props.parentEl],
  },
);

const alignX = ref<'left' | 'right'>('left');
const alignY = ref<'bottom' | 'top'>('bottom');

const onEnter = () => {
  const rect = el.value.getBoundingClientRect();
  if (rect.height + rect.y > window.innerHeight) {
    alignY.value = 'top';
  } else {
    alignY.value = 'bottom';
  }

  if (rect.width + rect.x > window.innerWidth - 8) {
    // The 8px is needed because of the scrollbar. 8px is the width of the scrollbar.
    alignX.value = 'right';
  } else {
    alignX.value = 'left';
  }
};

const onAfterLeave = () => {
  // Reset positioning to that the correct calculation happens when showing again
  alignX.value = 'left';
  alignY.value = 'bottom';
};
</script>

<style
  lang="scss"
  module
>
.base {
  position: absolute;
  max-width: calc(100vw - #{$spacing * 2});
  text-align: left;
  padding: 4px 0;
  z-index: 2;

  &.alignLeft {
    left: 0;
  }

  &.alignRight {
    right: 0;
  }

  &.alignBottom {
    top: 100%;
  }

  &.alignTop {
    bottom: 100%;
  }
}

.inner {
  border-radius: $radius;
  background: var(--color-bg);
  box-shadow: var(--shadow);
  overflow: hidden;

  .base:global(.v-enter-active) & {
    transition:
      transform 0.15s $easeOutBack,
      opacity 0.15s ease;
  }

  .base:global(.v-enter-from) & {
    transform: scale(0.8);
    opacity: 0;
  }
}

.option {
  display: flex;
  align-items: center;
  gap: $spacing * 0.5;
  padding: $spacing;
  cursor: pointer;
  background-color: var(--color-bg);
  color: var(--color-text);

  &:not(:last-child) {
    border-bottom: 1px solid var(--color-border);
  }

  @include hover {
    background-color: var(--color-highlight);
  }

  &.colorError {
    @include theme('light') {
      color: var(--color-error);
    }
    @include theme('dark') {
      color: var(--color-error-light);
    }
  }
}
</style>
